import type { Ref } from "vue";
import { onMounted, onUnmounted, onUpdated } from "vue";

// magic number to avoid repeated retrieval
const pageOffset = 56;

export function useActiveAnchor(container: Ref<HTMLElement | null>) {
	const onScroll = throttleAndDebounce(setActiveLink, 100);

	function setActiveLink(): void {
		const scrollableContainer = document.querySelector(".quick-nav-content");
		if (!scrollableContainer || !container.value) {
			return;
		}

		const links = [].slice.call(
			container.value.querySelectorAll("a"),
		) as HTMLAnchorElement[];

		const anchors = [].slice
			.call(document.querySelectorAll("fieldset"))
			.filter((anchor: HTMLAnchorElement) => {
				if (anchor.offsetParent === null) {
					return false;
				}

				return links.some((link) => link.hash === `#${anchor.id}`);
			}) as HTMLAnchorElement[];

		// page bottom - highlight last one
		if (
			anchors.length &&
			// https://github.com/vuejs/theme/pull/74
			scrollableContainer.scrollTop + scrollableContainer.clientHeight >=
				scrollableContainer.scrollHeight - 1
		) {
			const activeAnchor = anchors[anchors.length - 1];
			activateLink(activeAnchor ? `#${activeAnchor.id}` : null);
			return;
		}

		for (let i = 0; i < anchors.length; i++) {
			const anchor = anchors[i];
			if (!anchor) {
				continue;
			}

			const nextAnchor = anchors[i + 1];

			const [isActive, id] = isAnchorActive(i, anchor, nextAnchor);
			if (isActive) {
				activateLink(`#${id}`);
				return;
			}
		}

		function isAnchorActive(
			index: number,
			anchor: HTMLAnchorElement,
			nextAnchor: HTMLAnchorElement | undefined,
		): [boolean, string | null] {
			const scrollTop = scrollableContainer?.scrollTop ?? 0;

			if (index === 0 && scrollTop === 0) {
				return [true, null];
			}

			if (scrollTop < getAnchorTop(anchor)) {
				return [false, null];
			}

			if (!nextAnchor || scrollTop < getAnchorTop(nextAnchor)) {
				return [true, anchor.id];
			}

			return [false, null];
		}

		function getAnchorTop(anchor: HTMLAnchorElement): number {
			const anchorTop = anchor.getBoundingClientRect().top;
			const scrollableContainerTop = scrollableContainer?.scrollTop ?? 0;

			const offsetTop = anchorTop + scrollableContainerTop;

			return offsetTop - pageOffset - 15 - 16;
		}
	}

	onMounted(() => {
		requestAnimationFrame(setActiveLink);
		window.addEventListener("scroll", onScroll, true);
	});

	onUpdated(() => {
		// sidebar update means a route change
		activateLink(location.hash);
	});

	onUnmounted(() => {
		window.removeEventListener("scroll", onScroll);
	});

	let prevActiveLink: HTMLAnchorElement | null | undefined = null;

	function activateLink(hash: string | null): void {
		if (prevActiveLink) {
			prevActiveLink.classList.remove("active");
		}

		const activeLink = (prevActiveLink =
			hash == null
				? null
				: container.value?.querySelector(
						`a[href="${decodeURIComponent(hash)}"]`,
					));
		if (activeLink) {
			activeLink.classList.add("active");
		}
	}
}

function throttleAndDebounce(fn: () => void, delay: number): () => void {
	let timeout: any;
	let called = false;

	return () => {
		if (timeout) {
			clearTimeout(timeout);
		}

		if (!called) {
			fn();
			called = true;
			setTimeout(() => {
				called = false;
			}, delay);
		} else {
			timeout = setTimeout(fn, delay);
		}
	};
}
